// admin-api/lib/plan-review/planReviewMarkup.js
//
// Markup input validation, and the imagery witness constructor.
//
// ── WHY THE VOCABULARY IS HERE AND NOT INLINE IN THE ROUTE ──────────────────
//
// MARKUP_TYPES is a SECOND COPY of migration 113's
// plan_review_markup_type_chk. There is no way to have one copy: the constraint
// lives in the database and the 400 lives in Node. So the copy is named, put in
// one place, and PINNED BY A PROBE that reads pg_constraint and asserts set
// equality -- tests/manual/plan-review-markup.probe.mjs. Two copies that nothing
// compares is the defect; two copies that a check compares is a cache.
//
// The route validates BEFORE the insert for the reason route 4 states about the
// unique index: a feature whose input validation is a constraint violation
// reports a 500-shaped database error to a reviewer who mistyped a word, and it
// cannot distinguish "unknown type" from "note too short".
//
// ── THE WITNESS IS ALL FOUR OR NONE, IN CODE AS WELL AS IN THE CHECK ────────
//
// Migration 114 refuses a partial witness at the constraint. That constraint is
// the LAST line, not the first: a route that assembles three of four columns and
// lets the CHECK refuse it turns "no imagery covered this plan" into a 500.
//
// So buildImageryWitness returns a COMPLETE object or null, and never anything
// between. It is the one place that decides, which is why the route calls it
// rather than spreading a resolver result into an INSERT.
//
// It guards fields the schema currently declares NOT NULL
// (imagery_footprints.lod, datasets.acquisition_date, both verified NOT NULL and
// with zero null rows on 2026-09-17). That is deliberate: resolveDatasetForGeometry's
// own JSDoc declares them `string|null`, so its CONTRACT admits a value its
// present storage does not. Depending on a NOT NULL this module does not own is
// how the half-record arrives.

/**
 * Migration 113's plan_review_markup_type_chk, mirrored. Frozen for
 * PLAN_REVIEW_WRITER's reason: a vocabulary a caller can mutate is not one.
 */
export const MARKUP_TYPES = Object.freeze([
  "setback_violation", "surface_conflict", "reroute_required",
  "information_missing", "other",
]);

/** plan_review_markup_note_chk: length(btrim(note)) >= 10. */
export const MARKUP_NOTE_MIN = 10;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** GeoJSON geometry types PostGIS's ST_GeomFromGeoJSON accepts. */
const GEOJSON_TYPES = new Set([
  "Point", "LineString", "Polygon",
  "MultiPoint", "MultiLineString", "MultiPolygon", "GeometryCollection",
]);

/**
 * Validate a markup POST body.
 *
 * @returns {{ok: true, value: {featureId: string, markupType: string, note: string, geom: object|null}}
 *          |{ok: false, status: number, error: string, code: string}}
 */
export function checkMarkupInput(body) {
  const b = body ?? {};

  if (typeof b.feature_id !== "string" || !UUID_RE.test(b.feature_id)) {
    return { ok: false, status: 400, code: "feature_id_invalid",
      error: "feature_id must be the uuid of a feature on this review's current revision." };
  }

  if (!MARKUP_TYPES.includes(b.markup_type)) {
    return { ok: false, status: 400, code: "markup_type_unknown",
      error: `markup_type must be one of: ${MARKUP_TYPES.join(", ")}.` };
  }

  // btrim, matching the constraint. "          " is ten characters and no note.
  const note = typeof b.note === "string" ? b.note.trim() : "";
  if (note.length < MARKUP_NOTE_MIN) {
    return { ok: false, status: 400, code: "note_too_short",
      error: `note must be at least ${MARKUP_NOTE_MIN} characters after trimming; a finding the submitter cannot act on is not a finding.` };
  }

  // geom is OPTIONAL and is the REVIEWER'S OWN annotation -- a redline, a
  // corridor, a circled area. It is NEVER a mutation of the submitter's
  // geometry, which feature_id references and which migration 113 makes
  // immutable. null is the ordinary case: most findings are about the feature
  // as submitted.
  let geom = null;
  if (b.geom !== undefined && b.geom !== null) {
    if (typeof b.geom !== "object" || Array.isArray(b.geom) || !GEOJSON_TYPES.has(b.geom.type)) {
      return { ok: false, status: 400, code: "geom_invalid",
        error: "geom, when present, must be a GeoJSON geometry object." };
    }
    geom = b.geom;
  }

  return { ok: true, value: { featureId: b.feature_id, markupType: b.markup_type, note, geom } };
}

/**
 * Turn a resolveDatasetForGeometry result into the four witness columns, or
 * nothing at all.
 *
 * @param {{dataset_key?: string, acquisition_date?: string|Date|null, lod?: string|null}|null} resolved
 * @param {{resolvedAt?: Date}} [opts]
 * @returns {{dataset_key: string, acquisition_date: string|Date, lod: string, resolved_at: Date}|null}
 */
export function buildImageryWitness(resolved, { resolvedAt } = {}) {
  if (!resolved) return null;

  const { dataset_key: datasetKey, acquisition_date: acquisitionDate, lod } = resolved;

  // ANY missing fact drops the WHOLE witness. Not three columns and a null:
  // "we know the dataset but not when it was taken" is not a record of what the
  // reviewer was looking at, and storing it would assert one.
  if (typeof datasetKey !== "string" || !datasetKey) return null;
  if (acquisitionDate === null || acquisitionDate === undefined || acquisitionDate === "") return null;
  if (typeof lod !== "string" || !lod) return null;

  return {
    dataset_key: datasetKey,
    acquisition_date: acquisitionDate,
    lod,
    resolved_at: resolvedAt instanceof Date ? resolvedAt : new Date(),
  };
}
